import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'

import { cards } from '@/store/modules/cards'
import { claim } from '@/store/modules/claim'
import { security } from '@/store/modules/security'
import { notifications } from '@/store/modules/notifications'
import { store } from '@/store'
import { mock } from '@/tools'

Vue.use(VueRouter)

const securityModule = security.context(store)

const cardsModule = cards.context(store)
const claimModule = claim.context(store)
const notificationsModule = notifications.context(store)

export const mainRoutes = [
  {
    path: 'cards',
    name: 'main/cards',
    component: () => import(/* webpackChunkName: "cards" */ '@/views/main/Cards.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Управление картами',
      background: false,
      isAuthRequired: true,
      showTab: true,
    },
  },
  {
    path: 'transport',
    name: 'main/transport',
    component: () => import(/* webpackChunkName: "transport" */ '@/views/main/Transport.vue'),
    meta: Vue.observable({
      template: 'AppTemplate',
      title: 'Транспортный сервис',
      background: false,
      isAuthRequired: true,
      get showTab(): boolean {
        return securityModule.state.user?.isNoActiveNormalCard
      },
    }),
  },
]

const appBarRoutes: Array<RouteConfig> = [
  {
    path: 'loyalty',
    name: 'loyalty',
    component: () => import(/* webpackChunkName: "loyalty" */ '@/views/loyalty/Loyalty.vue'),
    redirect: { name: 'loyalty/certificates' },
    meta: {
      template: 'AppTemplate',
      background: false,
      isAuthRequired: true,
    },
    children: [
      {
        path: '/certificates',
        name: 'loyalty/certificates',
        component: () => import(/* webpackChunkName: "certificates" */ '@/views/loyalty/Certificates.vue'),
        meta: {
          template: 'AppTemplate',
          title: 'Мои сертификаты',
          background: false,
          isAuthRequired: true,
          hideTabs: true,
        },
      },
      {
        path: '/bonuses',
        name: 'loyalty/bonuses',
        component: () => import(/* webpackChunkName: "bonuses" */ '@/views/loyalty/Bonuses.vue'),
        meta: {
          template: 'AppTemplate',
          title: 'Бонусный счет',
          background: false,
          isAuthRequired: true,
          hideTabs: true,
        },
      },
    ],
  },
  {
    path: 'profile',
    name: 'profile',
    component: () => import(/* webpackChunkName: "profile" */ '@/views/ProfileForm.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Профиль',
      background: false,
      isAuthRequired: true,
      hideTabs: true,
    },
  },
  {
    path: 'notifications',
    name: 'notifications',
    component: () => import(/* webpackChunkName: "notifications" */ '@/views/notifications/Notifications.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Уведомления',
      background: false,
      isAuthRequired: true,
      hideTabs: true,
    },
  },
  {
    path: 'notifications/:notificationId',
    name: 'notifications/detail',
    component: () =>
      import(/* webpackChunkName: "notifications-detail" */ '@/views/notifications/NotificationDetail.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Просмотр уведомления',
      background: false,
      isAuthRequired: true,
      hideTabs: true,
    },
  },
]

const routes: Array<RouteConfig> = [
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '@/views/Login.vue'),

    meta: {
      template: 'AppTemplate',
      title: 'Вход',
      fillHeight: true,
      background: true,
      isAuthRequired: false,
    },
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import(/* webpackChunkName: "welcome" */ '@/views/Welcome.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Добро пожаловать',
      background: false,
      isAuthRequired: true,
    },
  },
  {
    path: '/refill',
    name: 'refill',
    component: () => import(/* webpackChunkName: "login" */ '@/views/TransportRefill.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Пополнение счета',
      fillHeight: true,
      background: false,
      isAuthRequired: true,
    },
    async beforeEnter(_to, _from, next) {
      if (await securityModule.actions.checkCabinetAccess().catch(mock)) {
        await notificationsModule.actions.enableCheckNotificationsLoop(true).catch(mock)
        next()
      } else next(false)
    },
  },
  {
    path: '/logout',
    name: 'logout',
    async beforeEnter(_to, _from, next) {
      await securityModule.actions.logout()
      next({ name: 'login' })
    },
    meta: {
      template: 'AppTemplate',
      title: 'Выход...',
      background: false,
      isAuthRequired: false,
    },
  },
  {
    path: '/connect/:qrData?',
    name: 'connect',
    component: () => import(/* webpackChunkName: "connect" */ '@/views/ConnectCardForm.vue'),
    props: route => ({ card: route.params.qrData ? cardsModule.state.cardToEdit : undefined }),
    meta: {
      template: 'AppTemplate',
      title: 'Подключить новую карту',
      fillHeight: true,
      background: false,
      isAuthRequired: true,
    },
    async beforeEnter(to, from, next) {
      if (to.params.qrData) await cardsModule.actions.getCardToEdit(to.params.qrData)
      if (!(await securityModule.actions.checkCabinetAccess())) next()
      else if (await cardsModule.actions.checkCardsActive().catch(mock)) next()
      else if (from.name === null) next({ name: 'main' })
      else next(false)
    },
  },
  {
    path: '/',
    name: 'main',
    component: () => import(/* webpackChunkName: "main" */ '@/views/main/Main.vue'),
    meta: {
      template: 'AppTemplate',
      background: false,
      isAuthRequired: true,
    },
    redirect: { name: 'main/cards' },
    children: [...mainRoutes, ...appBarRoutes],
    async beforeEnter(_to, _from, next) {
      if (await securityModule.actions.checkCabinetAccess()) {
        await notificationsModule.actions.enableCheckNotificationsLoop(true).catch(mock)
        next()
      } else await router.push({ name: 'welcome', replace: true })
    },
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.PUBLIC_PATH,
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition || { x: 0, y: 0 }
  },
})

router.beforeEach(async (to, _from, next) => {
  try {
    if (to.meta.isAuthRequired && !securityModule.state.user) {
      await securityModule.actions.getUser()
      await claimModule.actions.getCitizenList()
      if (securityModule.getters.isUser) next()
      else next({ name: 'login' })
    } else next()
  } catch (error) {
    next({ name: 'login' })
    mock(error)
  }
})

router.afterEach((to, _from) => {
  document.title = to.meta.title || process.env.VUE_APP_TITLE
})

export default router
